// billingController.js - Admin CRM Billing (#2539): what the project costs each month and where, from getBillingCosts
// (the Cloud Billing export, GitHub's billing, and the providers and spend caps read by hand), with what is still
// missing at the top. Admins keep each provider's plan and fixed cost in billing_providers/{id} (#2562), which this
// page reads and writes itself. The analysis behind it: docs/ops/monthly-costs.md.
import { BADGE, BUTTON, CARD, EMPTY_STATE } from "./ui/classes.js";
import { withBusy } from "./ui/busy.js";
import { hideDialog, showDialog } from "./ui/dialog.js";

const DAY_MS = 86400000;
// A plan typed more than six months ago is shown as one to check again: nothing updates it.
const STALE_AFTER_DAYS = 183;
// Renewals this close show under "Coming up".
const RENEWAL_WINDOW_DAYS = 60;
const PERIOD_TEXT = { month: " a month", year: " a year", once: " once" };

// Bars after the usage card's: yellow from 50 %, red from 75 % of the cap.
const BAR_TONE = {
  ok: "h-full rounded-full bg-emerald-500 dark:bg-emerald-400",
  warn: "h-full rounded-full bg-amber-500 dark:bg-amber-400",
  danger: "h-full rounded-full bg-red-600 dark:bg-red-500"
};
const STATUS_BADGE = {
  live: `<span class="${BADGE.green}">Read live</span>`,
  read: `<span class="${BADGE.blue}">Read by hand</span>`,
  entered: `<span class="${BADGE.blue}">Entered in the CRM</span>`,
  stale: `<span class="${BADGE.yellow}">Check again</span>`,
  unknown: `<span class="${BADGE.yellow}">Unknown</span>`
};
const TH = "px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white";
const TH_FIRST = "py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6 dark:text-white";
const TH_NUM = "px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-white";
const TD = "px-3 py-3 text-sm text-gray-500 dark:text-gray-400";
const TD_FIRST = "py-3 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6 dark:text-white";
const TD_NUM = "px-3 py-3 text-right text-sm whitespace-nowrap text-gray-500 tabular-nums dark:text-gray-400";
const TD_NUM_STRONG = "px-3 py-3 text-right text-sm font-semibold whitespace-nowrap text-gray-900 tabular-nums dark:text-white";
const ROW = "border-t border-gray-200 dark:border-white/10";
const GROUP_ROW = "bg-gray-50 py-2 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6 dark:bg-gray-800/50 dark:text-white";
const LINK = "font-semibold text-paprika-600 hover:text-paprika-800 dark:text-paprika-400 dark:hover:text-paprika-300";

const state = { deps: null, status: "idle", data: null, error: null, plans: {}, plansError: null };

function escapeHTML(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * `CHF 1,234.50` or `$4.50`; null and undefined are a dash. Not Intl's currency style, which puts a no-break space
 * after "CHF" in some ICU versions and not in others.
 */
export function money(amount, currency = "CHF") {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "—";
  const digits = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(amount));
  return `${amount < 0 ? "−" : ""}${currency === "USD" ? "$" : `${currency} `}${digits}`;
}

/** The same amount in USD at the export's rate (CHF per USD), or "" without one. */
export function inUsd(amount, rate) {
  if (typeof amount !== "number" || !rate) return "";
  return money(amount / rate, "USD");
}

/** "Oct 2026" from "2026-10". */
export function monthLabel(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

function toneFor(share) {
  if (share >= 0.75) return "danger";
  if (share >= 0.5) return "warn";
  return "ok";
}

function linkHtml({ label, url }) {
  const external = /^https:\/\//.test(url);
  return `<a href="${escapeHTML(url)}" class="${LINK}"${external ? ' target="_blank" rel="noopener"' : ""}>${escapeHTML(label)}</a>`;
}

function card(title, description, body, { id = "" } = {}) {
  return `
    <div class="${CARD.panel}"${id ? ` id="${id}"` : ""}>
      <div class="border-b border-gray-200 px-4 py-4 sm:px-6 dark:border-white/10">
        <h3 class="${CARD.title}">${title}</h3>
        ${description ? `<p class="${CARD.description}">${description}</p>` : ""}
      </div>
      ${body}
    </div>`;
}

function errorBox(what, message) {
  return `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">${escapeHTML(what)} couldn't be read: ${escapeHTML(message)}</div>`;
}

/** The to-do list: what is still missing for the page to be complete. */
export function todoHtml(todo = []) {
  if (!todo.length) return "";
  const items = todo.map((t, i) => `
    <li class="flex gap-x-3 px-4 py-4 sm:px-6">
      <span class="mt-0.5 flex size-6 flex-none items-center justify-center rounded-full bg-amber-50 text-xs font-semibold text-amber-800 inset-ring inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-400 dark:inset-ring-amber-400/20">${i + 1}</span>
      <div class="min-w-0 flex-auto">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">${escapeHTML(t.title)}</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">${escapeHTML(t.detail)}</p>
        ${t.links?.length ? `<p class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">${t.links.map(linkHtml).join("")}</p>` : ""}
      </div>
    </li>`).join("");
  return `
    <div class="${CARD.panel} outline-amber-400/60 dark:outline-amber-400/30" id="billingTodo">
      <div class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-4 sm:px-6 dark:border-white/10">
        <div>
          <h3 class="${CARD.title}">Still to do</h3>
          <p class="${CARD.description}">What this page can't show yet. Each item says where to look.</p>
        </div>
        <span class="${BADGE.yellow}">${todo.length} open</span>
      </div>
      <ul role="list" class="divide-y divide-gray-100 dark:divide-white/5">${items}</ul>
    </div>`;
}

/**
 * The providers with each one's saved plan (`billing_providers/{id}`, #2562) over the defaults from billing_costs.js.
 * A provider the page reads live keeps its amount; only its plan's name and note can be saved.
 */
export function mergeProviders(providers = [], plans = {}) {
  return providers.map((p) => {
    const saved = plans[p.id];
    if (!saved) return p;
    const live = p.status === "live";
    const cost = !live && typeof saved.amount === "number"
      ? { amount: saved.amount, currency: saved.currency || "USD", per: saved.period || "month" }
      : p.cost;
    return {
      ...p,
      plan: saved.plan || p.plan,
      note: saved.note || p.note,
      cost,
      renews_on: saved.renews_on || null,
      status: live ? "live" : "entered",
      saved_at: typeof saved.updated_at === "number" ? saved.updated_at : null,
      saved_by: saved.updated_by || null
    };
  });
}

/** A cost a month in CHF: a yearly one a twelfth, a one-off or unknown nothing; USD at the export's rate (CHF per USD). */
export function monthlyChf(cost, rate) {
  if (!cost || typeof cost.amount !== "number") return 0;
  const perMonth = cost.per === "month" ? cost.amount : cost.per === "year" ? cost.amount / 12 : 0;
  if (cost.currency === "CHF") return perMonth;
  return rate ? perMonth * rate : 0;
}

/** The fixed costs a month of the providers not read live, in CHF. */
export function fixedMonthlyChf(providers, rate) {
  return providers.filter((p) => p.status !== "live").reduce((sum, p) => sum + monthlyChf(p.cost, rate), 0);
}

export function isStale(p, now = Date.now()) {
  return typeof p.saved_at === "number" && now - p.saved_at > STALE_AFTER_DAYS * DAY_MS;
}

/** The renewals due within the next 60 days, or already past, soonest first, with the days left. */
export function upcomingRenewals(providers, now = Date.now()) {
  const today = Date.UTC(new Date(now).getUTCFullYear(), new Date(now).getUTCMonth(), new Date(now).getUTCDate());
  return providers
    .filter((p) => p.renews_on && Number.isFinite(Date.parse(p.renews_on)))
    .map((p) => ({ ...p, days: Math.round((Date.parse(p.renews_on) - today) / DAY_MS) }))
    .filter((p) => p.days <= RENEWAL_WINDOW_DAYS)
    .sort((a, b) => a.days - b.days);
}

/** What the month will cost in CHF: Google after credits (projected), GitHub, Upstash so far, and the fixed costs. */
export function expectedMonthChf(data, providers) {
  const g = data.google;
  const rate = g?.usd_rate || null;
  const google = g?.this_month ? (g.this_month.projected_net ?? g.this_month.net) : 0;
  const github = (data.github?.months?.at(-1)?.net || 0) * (rate || 0);
  const upstash = (data.upstash?.this_month || 0) * (rate || 0);
  return { live: google + github + upstash, fixed: fixedMonthlyChf(providers, rate) };
}

function stat(label, value, sub) {
  return `
    <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow-sm sm:p-6 dark:bg-gray-800/75 dark:inset-ring dark:inset-ring-white/10">
      <dt class="truncate text-sm font-medium text-gray-500 dark:text-gray-400">${label}</dt>
      <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums dark:text-white">${value}</dd>
      ${sub ? `<dd class="mt-1 text-sm text-gray-500 dark:text-gray-400">${sub}</dd>` : ""}
    </div>`;
}

/** This month's figures: Google usage, its projection, cash paid in top-ups, GitHub, the fixed costs and the total. */
export function statsHtml(data, providers = data.providers || []) {
  const g = data.google;
  const now = g?.this_month;
  const cur = g?.currency || "CHF";
  const rate = g?.usd_rate;
  const gh = data.github?.months?.at(-1);
  const cards = [
    now
      ? stat("Google usage so far", money(now.gross, cur), `${money(now.net, cur)} after credits · ${inUsd(now.gross, rate)}`)
      : stat("Google usage so far", "—", "The billing export couldn't be read."),
    now
      ? stat("Projected for the month", money(now.projected_gross, cur), now.projected_net === null ? "Too early in the month to project." : `${money(now.projected_net, cur)} after credits, at the pace so far`)
      : stat("Projected for the month", "—", ""),
    now
      ? stat("Paid in cash this month", money(now.prepaid_topups, cur), "AI Studio top-ups, already in the usage above")
      : stat("Paid in cash this month", "—", ""),
    gh
      ? stat("GitHub Actions", money(gh.net, "USD"), data.github.budget !== null ? `of the ${money(data.github.budget, "USD")} budget${data.github.budget_stops_actions ? ", which stops Actions" : ""}` : "No Actions budget set")
      : stat("GitHub Actions", "—", escapeHTML(data.github_error || ""))
  ];
  const expected = expectedMonthChf(data, providers);
  const entered = providers.filter((p) => p.status !== "live" && p.cost && monthlyChf(p.cost, rate) > 0).length;
  cards.push(
    stat("Fixed costs a month", money(expected.fixed, "CHF"), `${entered} ${entered === 1 ? "plan" : "plans"} with a recurring price; a yearly one counts a twelfth`),
    stat("Expected this month", money(expected.live + expected.fixed, "CHF"), `${money(expected.live, "CHF")} read live (Google after credits, GitHub, Upstash) + ${money(expected.fixed, "CHF")} fixed`)
  );
  const month = now ? monthLabel(now.month) : "This month";
  return `
    <div>
      <h3 class="${CARD.title}">${escapeHTML(month)}, on Pacific time as Google bills</h3>
      <dl class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">${cards.join("")}</dl>
    </div>`;
}

/** The spend caps, each with how full this month's gross spend has it. */
export function capsHtml(data) {
  const caps = data.google?.spend_caps;
  if (!caps) return "";
  const rows = caps.map((c) => {
    const pct = Math.round(c.share * 100);
    return `
      <tr class="${ROW}">
        <td class="${TD_FIRST}">${escapeHTML(c.label)}<p class="mt-1 text-xs font-normal text-gray-500 dark:text-gray-400">At 100 % it pauses ${escapeHTML(c.stops)}.</p></td>
        <td class="${TD_NUM}">${money(c.used, c.currency)}</td>
        <td class="${TD_NUM}">${money(c.amount, c.currency)}</td>
        <td class="w-40 px-3 py-3 pr-4 sm:pr-6">
          <div class="flex items-center gap-2">
            <div class="h-2 flex-auto overflow-hidden rounded-full bg-gray-100 dark:bg-white/10"><div class="${BAR_TONE[toneFor(c.share)]}" style="width: ${Math.min(100, pct)}%"></div></div>
            <span class="w-10 text-right text-sm font-semibold text-gray-900 tabular-nums dark:text-white">${pct} %</span>
          </div>
        </td>
      </tr>`;
  }).join("");
  return card(
    "Spend caps",
    `Google pauses a service in the project when its month's gross spend, before credits, reaches the cap. Amounts copied by hand from the billing account on ${escapeHTML(data.spend_caps_read_on)}; change them in Billing → Budgets &amp; alerts.`,
    `<div class="relative overflow-x-auto"><table class="min-w-full">
      <thead><tr><th scope="col" class="${TH_FIRST}">Capped</th><th scope="col" class="${TH_NUM}">This month</th><th scope="col" class="${TH_NUM}">Cap</th><th scope="col" class="${TH} pr-4 sm:pr-6">Used</th></tr></thead>
      <tbody>${rows}</tbody></table></div>`
  );
}

/** The months from the first one with any cost, of the six the function reads. */
export function monthsWithCosts(data) {
  const months = data.google?.months || [];
  const githubNet = (month) => data.github?.months?.find((m) => m.month === month)?.net || 0;
  const first = months.findIndex((m) => m.gross || m.net || m.prepaid_topups || githubNet(m.month));
  return first < 0 ? months.slice(-1) : months.slice(first);
}

/** The months by category, gross and after credits, with the cash rows apart. */
export function monthsHtml(data) {
  if (!data.google) return "";
  const g = { ...data.google, months: monthsWithCosts(data) };
  const githubMonths = g.months.map((m) => data.github?.months?.find((x) => x.month === m.month) || { month: m.month, net: 0 });
  const cur = g.currency || "CHF";
  const head = g.months.map((m) => `<th scope="col" class="${TH_NUM}">${escapeHTML(monthLabel(m.month))}${m.current ? '<span class="block text-xs font-normal text-gray-500 dark:text-gray-400">so far</span>' : ""}</th>`).join("");
  const cells = (pick, strong = false) => g.months.map((m) => `<td class="${strong ? TD_NUM_STRONG : TD_NUM}">${money(pick(m), cur)}</td>`).join("");
  const span = g.months.length + 1;
  const categoryRows = g.months[0].categories.map((c, i) => `
    <tr class="${ROW}"><td class="${TD_FIRST}">${escapeHTML(c.label)}</td>${cells((m) => m.categories[i].gross)}</tr>`).join("");
  const github = data.github
    ? `<tr class="${ROW}"><td class="${TD_FIRST}">GitHub Actions</td>${githubMonths.map((m) => `<td class="${TD_NUM}">${money(m.net, "USD")}</td>`).join("")}</tr>`
    : `<tr class="${ROW}"><td class="${TD_FIRST}">GitHub Actions</td><td colspan="${g.months.length}" class="${TD}">${escapeHTML(data.github_error || "")}</td></tr>`;
  return card(
    "Month by month",
    "Google's usage before credits, by what it is for, over up to six months. The export starts on 2026-08-31, when the project moved to this billing account.",
    `<div class="relative overflow-x-auto"><table class="min-w-full">
      <thead><tr><th scope="col" class="${TH_FIRST}"><span class="sr-only">Cost</span></th>${head}</tr></thead>
      <tbody>
        <tr class="${ROW}"><th scope="colgroup" colspan="${span}" class="${GROUP_ROW}">Google Cloud, ${escapeHTML(cur)}</th></tr>
        ${categoryRows}
        <tr class="${ROW}"><td class="${TD_FIRST}">Total before credits</td>${cells((m) => m.gross, true)}</tr>
        <tr class="${ROW}"><td class="${TD_FIRST}">Total after credits</td>${cells((m) => m.net, true)}</tr>
        <tr class="${ROW}"><th scope="colgroup" colspan="${span}" class="${GROUP_ROW}">Cash paid</th></tr>
        <tr class="${ROW}"><td class="${TD_FIRST}">AI Studio top-ups<p class="mt-1 text-xs font-normal text-gray-500 dark:text-gray-400">Prepaid; the Gemini API row above is what they paid for.</p></td>${cells((m) => m.prepaid_topups)}</tr>
        <tr class="${ROW}"><th scope="colgroup" colspan="${span}" class="${GROUP_ROW}">GitHub, USD</th></tr>
        ${github}
      </tbody></table></div>`
  );
}

/** This month's Google services, gross and after credits. */
export function servicesHtml(data) {
  const now = data.google?.this_month;
  if (!now?.services?.length) return "";
  const cur = data.google.currency || "CHF";
  const rows = now.services.map((s) => `
    <tr class="${ROW}"><td class="${TD_FIRST}">${escapeHTML(s.name)}</td><td class="${TD_NUM}">${money(s.gross, cur)}</td><td class="${TD_NUM} pr-4 sm:pr-6">${money(s.net, cur)}</td></tr>`).join("");
  const credits = (data.google.credits || []).map((c) => `
    <tr class="${ROW}"><td class="${TD_FIRST}">${escapeHTML(c.name)}</td><td class="${TD_NUM}">${money(c.this_month, cur)}</td><td class="${TD_NUM} pr-4 sm:pr-6">${money(c.total, cur)}</td></tr>`).join("");
  return card(
    "This month by service",
    `What the export has so far, about ${Math.round(now.covered_share * 100)} % of the month.`,
    `<div class="relative overflow-x-auto"><table class="min-w-full">
      <thead><tr><th scope="col" class="${TH_FIRST}">Service</th><th scope="col" class="${TH_NUM}">Before credits</th><th scope="col" class="${TH_NUM} pr-4 sm:pr-6">After credits</th></tr></thead>
      <tbody>${rows}
        ${credits ? `<tr class="${ROW}"><th scope="colgroup" colspan="3" class="${GROUP_ROW}">Credits: this month, and since the export began</th></tr>${credits}` : ""}
      </tbody></table></div>`
  );
}

/** Coming renewals from the plans entered in the CRM. */
export function renewalsHtml(providers, now = Date.now()) {
  const due = upcomingRenewals(providers, now);
  if (!due.length) return "";
  const when = (d) => (d < 0 ? `${-d} days ago` : d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);
  const rows = due.map((p) => `
    <tr class="${ROW}"><td class="${TD_FIRST}">${escapeHTML(p.name)}</td><td class="${TD}">${escapeHTML(p.renews_on)}, ${when(p.days)}</td><td class="${TD_NUM} pr-4 sm:pr-6">${p.cost ? `${money(p.cost.amount, p.cost.currency)}${PERIOD_TEXT[p.cost.per] || ""}` : "—"}</td></tr>`).join("");
  return card(
    "Coming up",
    `Renewals in the next ${RENEWAL_WINDOW_DAYS} days, from the dates entered below.`,
    `<div class="relative overflow-x-auto"><table class="min-w-full"><tbody>${rows}</tbody></table></div>`
  );
}

/**
 * Every provider: its plan, what it costs, whether the page reads it, and Edit for its plan (#2562). Upstash's cost
 * comes from `data` (#2554).
 */
export function providersHtml(providers = [], data = {}, { now = Date.now(), plansError = null } = {}) {
  const cost = (p) => {
    if (p.live === "upstash") {
      return data.upstash
        ? `${money(data.upstash.this_month, data.upstash.currency)}<span class="block text-xs">so far this month</span>`
        : `<span class="ml-auto block max-w-56 text-xs whitespace-normal text-red-600 dark:text-red-400">${escapeHTML(data.upstash_error || "—")}</span>`;
    }
    if (!p.cost) return p.status === "live" ? "Above" : "—";
    return `${money(p.cost.amount, p.cost.currency)}${PERIOD_TEXT[p.cost.per] || ""}`;
  };
  const status = (p) => {
    if (p.status === "entered") {
      const date = p.saved_at ? new Date(p.saved_at).toISOString().slice(0, 10) : "";
      return `${isStale(p, now) ? STATUS_BADGE.stale : STATUS_BADGE.entered}${date ? `<span class="mt-1 block text-xs">${escapeHTML(date)}${p.saved_by ? ` · ${escapeHTML(p.saved_by)}` : ""}</span>` : ""}`;
    }
    return `${STATUS_BADGE[p.status] || ""}${p.read_on ? `<span class="mt-1 block text-xs">${escapeHTML(p.read_on)}</span>` : ""}`;
  };
  const rows = providers.map((p) => `
    <tr class="${ROW}">
      <td class="${TD_FIRST}">${escapeHTML(p.name)}</td>
      <td class="${TD}">${escapeHTML(p.plan)}${p.note ? `<p class="mt-1 text-xs">${escapeHTML(p.note)}</p>` : ""}${p.renews_on ? `<p class="mt-1 text-xs">Renews ${escapeHTML(p.renews_on)}</p>` : ""}</td>
      <td class="${TD_NUM} whitespace-normal">${cost(p)}</td>
      <td class="${TD} whitespace-nowrap">${status(p)}</td>
      <td class="px-3 py-3 pr-4 text-right text-sm whitespace-nowrap sm:pr-6">
        ${p.id ? `<button type="button" data-billing-edit="${escapeHTML(p.id)}" class="${BUTTON.small}">Edit<span class="sr-only">, ${escapeHTML(p.name)}</span></button>` : ""}
        ${p.link ? `<span class="ml-3">${linkHtml({ label: "Billing", url: p.link })}</span>` : ""}
      </td>
    </tr>`).join("");
  return card(
    "Providers",
    "Everyone the project pays or could pay. Edit keeps a provider's plan, price and renewal here; a price that changes with use can't be entered, only a recurring one.",
    `${plansError ? `<div class="px-4 pt-4 sm:px-6">${errorBox("The plans entered in the CRM", plansError)}</div>` : ""}
    <div class="relative overflow-x-auto"><table class="min-w-full">
      <thead><tr><th scope="col" class="${TH_FIRST}">Provider</th><th scope="col" class="${TH}">Plan</th><th scope="col" class="${TH_NUM}">Cost</th><th scope="col" class="${TH}">Status</th><th scope="col" class="py-3.5 pr-4 pl-3 sm:pr-6"><span class="sr-only">Actions</span></th></tr></thead>
      <tbody>${rows}</tbody></table></div>`
  );
}

/** The whole page for a getBillingCosts result and the plans saved in the CRM. */
export function renderBilling(data, plans = {}, { now = Date.now(), plansError = null } = {}) {
  const providers = mergeProviders(data.providers, plans);
  return [
    todoHtml(data.todo),
    data.google_error ? errorBox("The Cloud Billing export", data.google_error) : "",
    statsHtml(data, providers),
    renewalsHtml(providers, now),
    capsHtml(data),
    monthsHtml(data),
    servicesHtml(data),
    providersHtml(providers, data, { now, plansError })
  ].filter(Boolean).join("");
}

function metaText() {
  if (state.status === "loading") return "Reading the billing export and GitHub…";
  if (state.status === "error") return "";
  const asOf = state.data?.google?.as_of;
  return asOf ? `Export up to ${new Date(asOf).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}${state.data.cached ? " · cached" : ""}` : "";
}

function draw() {
  const results = document.getElementById("billingResults");
  const meta = document.getElementById("billingMeta");
  if (meta) meta.textContent = metaText();
  if (!results) return;
  if (state.status === "error") {
    results.innerHTML = errorBox("Billing", state.error);
  } else if (state.data) {
    results.innerHTML = renderBilling(state.data, state.plans, { plansError: state.plansError });
  } else {
    results.innerHTML = `<div class="${EMPTY_STATE.box}"><p class="${EMPTY_STATE.title}">Reading the costs…</p></div>`;
  }
}

/** The plans saved in the CRM, by provider id, each `updated_at` in milliseconds. */
async function readPlans() {
  const { db, getDocs, collection } = state.deps;
  try {
    const snap = await getDocs(collection(db, "billing_providers"));
    const plans = {};
    snap.forEach((d) => {
      const v = d.data();
      plans[d.id] = { ...v, updated_at: v.updated_at?.toMillis?.() ?? null };
    });
    state.plans = plans;
    state.plansError = null;
  } catch (err) {
    console.error("❌ Reading billing_providers failed:", err);
    state.plansError = err?.message || String(err);
  }
}

async function load({ refresh = false } = {}) {
  const deps = state.deps;
  if (!deps?.functions || !deps?.httpsCallable || state.status === "loading") return;
  state.status = "loading";
  draw();
  try {
    const [{ data }] = await Promise.all([deps.httpsCallable(deps.functions, "getBillingCosts")({ refresh }), readPlans()]);
    state.data = data;
    state.status = "ok";
  } catch (err) {
    console.error("❌ getBillingCosts failed:", err);
    state.error = err?.message || String(err);
    state.status = "error";
    deps.showToast?.(`Could not read the costs: ${state.error}`, "error");
  }
  draw();
}

function field(id) {
  return document.getElementById(id);
}

/** Opens the plan editor for one provider, filled with what is saved, else the defaults. */
export function openPlanEditor(id) {
  const provider = (state.data?.providers || []).find((p) => p.id === id);
  if (!provider) return;
  const saved = state.plans[id] || {};
  const live = provider.status === "live";
  const cost = !live ? (typeof saved.amount === "number" ? saved : provider.cost ? { amount: provider.cost.amount, currency: provider.cost.currency, period: provider.cost.per } : {}) : {};
  field("billingPlanId").value = id;
  field("billingPlanTitle").textContent = `${provider.name}: plan`;
  // An unknown provider's text in the code ("Unknown …") is no plan name to keep.
  field("billingPlanName").value = saved.plan || (provider.status === "unknown" ? "" : provider.plan || "");
  field("billingPlanAmount").value = typeof cost.amount === "number" ? String(cost.amount) : "";
  field("billingPlanCurrency").value = cost.currency || "USD";
  field("billingPlanPeriod").value = cost.period || "month";
  field("billingPlanRenews").value = saved.renews_on || "";
  field("billingPlanNote").value = saved.note || "";
  field("billingPlanCostFields").hidden = live;
  field("billingPlanLiveHelp").hidden = !live;
  field("billingPlanClearBtn").hidden = !state.plans[id];
  showDialog("billingPlanDialog");
}

/** The document the form describes, as the rules accept it. Exported for the tests. */
export function planFromForm(values, { live = false, email = null, serverTimestamp }) {
  const text = (v) => (String(v ?? "").trim() || null);
  const plan = { plan: text(values.plan), renews_on: text(values.renews_on), note: text(values.note), updated_at: serverTimestamp(), updated_by: email };
  if (!live) {
    const amount = String(values.amount ?? "").trim() === "" ? null : Number(values.amount);
    if (amount !== null && (!Number.isFinite(amount) || amount < 0)) throw new Error("The amount must be a number of 0 or more.");
    Object.assign(plan, { amount, currency: values.currency === "CHF" ? "CHF" : "USD", period: ["month", "year", "once"].includes(values.period) ? values.period : "month" });
  }
  return plan;
}

async function savePlan() {
  const { db, doc, setDoc, serverTimestamp, auth, showToast } = state.deps;
  const id = field("billingPlanId").value;
  const provider = (state.data?.providers || []).find((p) => p.id === id);
  if (!provider) return;
  let plan;
  try {
    plan = planFromForm({
      plan: field("billingPlanName").value,
      amount: field("billingPlanAmount").value,
      currency: field("billingPlanCurrency").value,
      period: field("billingPlanPeriod").value,
      renews_on: field("billingPlanRenews").value,
      note: field("billingPlanNote").value
    }, { live: provider.status === "live", email: auth?.currentUser?.email || null, serverTimestamp });
  } catch (err) {
    showToast?.(err.message, "error");
    return;
  }
  await withBusy(field("billingPlanSaveBtn"), async () => {
    try {
      await setDoc(doc(db, "billing_providers", id), plan);
      hideDialog("billingPlanDialog");
      await readPlans();
      draw();
      showToast?.(`Saved ${provider.name}'s plan.`, "success");
    } catch (err) {
      console.error("❌ Saving the plan failed:", err);
      showToast?.(`Could not save the plan: ${err.message}`, "error");
    }
  });
}

async function clearPlan() {
  const { db, doc, deleteDoc, showToast } = state.deps;
  const id = field("billingPlanId").value;
  await withBusy(field("billingPlanClearBtn"), async () => {
    try {
      await deleteDoc(doc(db, "billing_providers", id));
      hideDialog("billingPlanDialog");
      await readPlans();
      draw();
      showToast?.("Back to the plan kept in the code.", "success");
    } catch (err) {
      console.error("❌ Clearing the plan failed:", err);
      showToast?.(`Could not clear the plan: ${err.message}`, "error");
    }
  });
}

/** Opening the section reads the costs and the saved plans once; Refresh reads them again past the function's cache. */
export function openBilling() {
  if (state.status === "idle" || state.status === "error") load();
}

export async function loadBillingTemplate() {
  const containerEl = document.getElementById("sectionBillingContainer");
  if (!containerEl) return false;
  try {
    const res = await fetch("templates/billing.html");
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching billing.html`);
    containerEl.innerHTML = await res.text();
    return true;
  } catch (err) {
    console.error("❌ Failed to load Billing template:", err);
    return false;
  }
}

// Binds Refresh, Edit and the plan editor, and reads nothing: the template is mounted before anyone has signed in
// (#1333), and the costs are read when an admin opens the section.
export function initBillingController(deps) {
  state.deps = deps;
  const button = document.getElementById("billingRefreshBtn");
  button?.addEventListener("click", () => withBusy(button, () => load({ refresh: true })));
  document.getElementById("billingResults")?.addEventListener("click", (e) => {
    const edit = e.target.closest?.("[data-billing-edit]");
    if (edit) openPlanEditor(edit.dataset.billingEdit);
  });
  document.getElementById("billingPlanForm")?.addEventListener("submit", (e) => {
    e.preventDefault();
    savePlan();
  });
  document.getElementById("billingPlanClearBtn")?.addEventListener("click", () => clearPlan());
}
